import { CombinedGraphQLErrors } from "@apollo/client/errors";
import * as Sentry from "@sentry/react";
import { notFound } from "@tanstack/react-router";
import i18n from "i18next";
import type { TypedDocumentNode } from "@apollo/client";
import type { ParsedLocation } from "@tanstack/react-router";
import type { ShowMetaSource, ShowMetaTarget } from "@/lib/showMetaTags";
import { apolloClient } from "@/api/apiClient";
import { applyShowMetaTags } from "@/hooks/useShowMetaTags";
import {
  SEO_TITLE_SUFFIX_KEY,
  deriveShowMetaTags,
  showMetaInput,
} from "@/lib/showMetaTags";
import { isShowNotFound } from "@/lib/showNotFound";
import { parseSeriesParam } from "@/lib/showSlug";

/**
 * The loader contract shared by the two pages a show URL can open — the
 * player (`/{series}/{episode}`) and the series details page
 * (`/series/{series}`, SHO-757): resolve the show or 404, and apply its tags.
 *
 * The tags are applied before the route resolves — GA4's page_view (fired
 * from router.subscribe("onResolved") in main.tsx) reads document.title
 * synchronously as soon as the router is done loading, which is otherwise
 * before the page's `useShowMetaTags` effect has run. `preload` is true for
 * hover/intent prefetch: skip applying tags then, or hovering a show link
 * would retitle the page still being viewed.
 */
export const loadShowOrNotFound = async <
  TData extends { show: ShowMetaSource },
>(
  query: TypedDocumentNode<TData, { showId: string }>,
  seriesParam: string,
  {
    preload,
    location,
    target,
  }: { preload: boolean; location: ParsedLocation; target: ShowMetaTarget },
): Promise<{ showId: string }> => {
  // Neither slugged nor a numeric id (`/video/0`, a truncated link): a 404
  // on the URL's shape, decided here without the query the API would refuse
  // and Sentry would then report. A well-formed id that doesn't exist still
  // goes through the query. A loader's `notFound()` is rendered by the
  // throwing route's own `notFoundComponent`, so each route sets one.
  const showId = parseSeriesParam(seriesParam);
  if (!showId) throw notFound();

  // That well-formed id is a 404 as well once the API says no show matches
  // it (a stale shared or campaign link), not the router's error screen.
  const { data } = await apolloClient
    .query({ query, variables: { showId } })
    .catch((error: unknown) => {
      if (
        !CombinedGraphQLErrors.is(error) ||
        !error.errors.some(isShowNotFound)
      )
        throw error;
      // The `ErrorLink` no longer reports that error, so a link the app
      // itself rendered to a dead show — a rail, My List, Continue watching —
      // must be reported here or nothing says the catalog serves one. A
      // navigation is in-app when the history entry was pushed by the router:
      // `__TSR_index` is 0 on the entry the tab landed on (which a deeplink's
      // redirect replaces, not pushes) — the same test as
      // `history.canGoBack()`. Not on preload: the click that follows a hover
      // runs the loader again and would report the same link twice.
      if (!preload && location.state.__TSR_index !== 0)
        Sentry.captureMessage("In-app link to an unknown show", {
          level: "warning",
          extra: { showId },
        });
      // The page never mounts on a 404, so `useShowMetaTags`' teardown never
      // runs: on the prerendered page of a show removed since the last build,
      // the show's title, canonical and JSON-LD would stay in the `<head>`
      // above "Page not found" — a soft 404 that still reads as the show.
      // Same guard as the apply below: a preload must not retitle the page
      // being viewed.
      if (!preload) applyShowMetaTags(null);
      throw notFound();
    });

  if (!preload && data?.show)
    applyShowMetaTags(
      deriveShowMetaTags(
        showMetaInput(
          data.show,
          target,
          window.location.origin,
          i18n.t(SEO_TITLE_SUFFIX_KEY[target]),
        ),
      ),
    );

  return { showId };
};
